import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { UserPlus, Copy, ArrowSquareOut, Info, CheckCircle } from "@phosphor-icons/react";
import { useRbac } from "../../lib/rbac";
import { teamLoginUrl } from "../../lib/appUrls";

// Phase 2.3 — Team Access admin page.
// Tabs: Team Members · Pending Invitations · Roles · Permission Matrix · Access Audit.
// Server-side enforcement lives in `rbac_middleware.py`; this UI hides
// tabs the user cannot access based on `/api/rbac/me`.

// Short role descriptions surfaced in the invite dropdown so operators
// don't have to guess what each role allows. Keys must match role_id.
const ROLE_DESCRIPTIONS = {
  role_super_admin:           "Full access — every module and every action.",
  role_transactions_viewer:   "View and download transactions only.",
  role_transaction_operator:  "Upload and review transactions.",
  role_pricing_manager:       "Manage pricing and margins.",
  role_customer_card_manager: "Manage customers and fuel cards.",
  role_finance_viewer:        "View dashboard, AP, AR and profitability.",
  role_support_agent:         "Manage support tickets.",
};

const TABS = [
  { key: "members",   label: "Team Members",       perm: "users.view"      },
  { key: "invites",   label: "Pending Invitations", perm: "users.view"     },
  { key: "roles",     label: "Roles",              perm: "roles.view"      },
  { key: "matrix",    label: "Permission Matrix",  perm: "roles.view"      },
  { key: "audit",     label: "Access Audit",       perm: "audit_log.view"  },
];

export default function TeamAccess() {
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("members");
  const [users, setUsers]           = useState([]);
  const [roles, setRoles]           = useState([]);
  const [invites, setInvites]       = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [audit, setAudit]           = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteResult, setInviteResult] = useState(null); // { accept_path, email, raw_token }

  const canInvite = me?.permissions?.includes("users.invite")
                 || me?.permissions?.includes("users.create")
                 || me?.role_ids?.includes("role_super_admin");

  const loadAll = async () => {
    try {
      const [m, u, r, p, a, i] = await Promise.all([
        api.get("/rbac/me"),
        api.get("/rbac/users").catch(() => ({ data: { users: [] } })),
        api.get("/rbac/roles").catch(() => ({ data: { roles: [] } })),
        api.get("/rbac/permissions").catch(() => ({ data: { permissions: [] } })),
        api.get("/rbac/audit").catch(() => ({ data: { events: [] } })),
        api.get("/rbac/invitations").catch(() => ({ data: { invitations: [] } })),
      ]);
      setMe(m.data);
      setUsers(u.data.users || []);
      setRoles(r.data.roles || []);
      setPermissions(p.data.permissions || []);
      setAudit(a.data.events || []);
      setInvites(i.data.invitations || []);
    } catch (e) {
      toast.error("Failed to load Team Access data");
    }
  };
  useEffect(() => { loadAll(); }, []);

  const visibleTabs = TABS.filter(t =>
    me?.permissions?.includes(t.perm) || me?.role_ids?.includes("role_super_admin")
  );

  return (
    <div className="p-6 space-y-4" data-testid="team-access-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Team Access</h1>
          <p className="text-xs text-zinc-500 mt-1">
            Manage staff logins, roles, and permissions. Customer portal users are isolated.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <PreviewRoleControl roles={roles} />
          {canInvite && (
            <button
              className="gf-btn gf-btn-primary text-base px-5 py-2.5 shadow-sm"
              onClick={() => setShowInvite(true)}
              data-testid="btn-add-team-user"
            >
              <UserPlus size={16} weight="bold" /> Add Team User
            </button>
          )}
        </div>
      </div>

      {canInvite && (
        <HowToAddBox />
      )}

      <div className="border-b border-zinc-200 flex gap-1">
        {visibleTabs.map(t => (
          <button
            key={t.key}
            className={`px-4 py-2 text-sm ${tab === t.key ? "border-b-2 border-black font-medium" : "text-zinc-500"}`}
            onClick={() => setTab(t.key)}
            data-testid={`tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "members" && (
        <MembersTab users={users} roles={roles} me={me} onReload={loadAll}
                    onInvite={() => setShowInvite(true)} canInvite={canInvite} />
      )}
      {tab === "invites" && (
        <InvitesTab invites={invites} roles={roles} onReload={loadAll}
                    onOpenResult={setInviteResult} />
      )}
      {tab === "roles" && (
        <RolesTab roles={roles} permissions={permissions} />
      )}
      {tab === "matrix" && (
        <PermissionMatrix roles={roles} permissions={permissions} />
      )}
      {tab === "audit" && (
        <AuditTab events={audit} />
      )}

      {showInvite && (
        <InviteModal
          roles={roles}
          onClose={() => setShowInvite(false)}
          onCreated={(res) => { setInviteResult(res); loadAll(); }}
        />
      )}
      {inviteResult && (
        <InviteResultModal
          result={inviteResult}
          roles={roles}
          onClose={() => setInviteResult(null)}
        />
      )}
    </div>
  );
}

function MembersTab({ users, roles, me, onReload, onInvite, canInvite }) {
  const roleMap = Object.fromEntries(roles.map(r => [r.role_id, r.name]));
  const toggle = async (u) => {
    try {
      if (u.disabled) {
        await api.post(`/rbac/users/${u.user_id}/enable`);
        toast.success(`Enabled ${u.email}`);
      } else {
        await api.patch(`/rbac/users/${u.user_id}`, { disabled: true });
        toast.success(`Disabled ${u.email}`);
      }
      onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  if (!users || users.length === 0) {
    return (
      <div
        className="border border-dashed border-zinc-300 bg-zinc-50 p-8 text-center rounded-sm"
        data-testid="members-empty-state"
      >
        <div className="text-sm text-zinc-700 font-medium mb-1">
          No team users have been added yet.
        </div>
        <div className="text-xs text-zinc-500 mb-4">
          Add your first teammate to give them access to the admin console.
        </div>
        {canInvite && (
          <button
            className="gf-btn gf-btn-primary"
            onClick={onInvite}
            data-testid="btn-add-team-user-empty"
          >
            <UserPlus size={14} weight="bold" /> Add Team User
          </button>
        )}
      </div>
    );
  }

  return (
    <table className="w-full text-sm border border-zinc-200" data-testid="members-table">
      <thead className="bg-zinc-50 text-zinc-600">
        <tr>
          <th className="p-2 text-left">Name</th>
          <th className="p-2 text-left">Email</th>
          <th className="p-2 text-left">Roles</th>
          <th className="p-2 text-left">Status</th>
          <th className="p-2 text-left">Last Login</th>
          <th className="p-2 text-left">Created</th>
          <th className="p-2 text-right">Actions</th>
        </tr>
      </thead>
      <tbody>
        {users.map(u => (
          <tr key={u.user_id} className="border-t border-zinc-200" data-testid={`row-user-${u.user_id}`}>
            <td className="p-2">{u.name || "—"}</td>
            <td className="p-2 font-mono">{u.email}</td>
            <td className="p-2">{(u.role_ids || []).map(rid => roleMap[rid] || rid).join(", ") || "—"}</td>
            <td className="p-2">{u.disabled ? <span className="text-red-600">Disabled</span> : <span className="text-emerald-600">Active</span>}</td>
            <td className="p-2 text-zinc-500">{u.last_login_at?.slice(0, 10) || "—"}</td>
            <td className="p-2 text-zinc-500">{u.created_at?.slice(0, 10) || "—"}</td>
            <td className="p-2 text-right">
              <button
                className="gf-btn gf-btn-outline text-xs"
                onClick={() => toggle(u)}
                disabled={me?.user_id === u.user_id && !u.disabled}
                data-testid={`btn-toggle-${u.user_id}`}
                title={me?.user_id === u.user_id && !u.disabled
                  ? "You cannot disable your own account"
                  : (u.disabled ? "Re-enable this user" : "Disable this user and revoke their sessions")}
              >
                {u.disabled ? "Enable" : "Disable"}
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function InvitesTab({ invites, roles, onReload, onOpenResult }) {
  const roleMap = Object.fromEntries(roles.map(r => [r.role_id, r.name]));

  const isExpired = (i) => {
    if (i.status === "expired") return true;
    if (i.status !== "pending") return false;
    return i.expires_at && new Date(i.expires_at).getTime() < Date.now();
  };

  const doAction = async (id, action) => {
    try {
      const { data } = await api.post(`/rbac/invitations/${id}/${action}`);
      if (data.raw_token) {
        // Surface the fresh link in the polished result modal so the
        // operator can copy/open it with one click.
        onOpenResult({
          invitation_id: id,
          email: data.email,
          first_name: data.first_name,
          last_name: data.last_name,
          assigned_roles: data.assigned_roles,
          expires_at: data.expires_at,
          raw_token: data.raw_token,
          accept_path: data.accept_path || `/invite/accept?token=${data.raw_token}`,
        });
      } else {
        toast.success(`Invitation ${action}led`);
      }
      onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  const copyLink = (i) => {
    // Live invitations don't retain their raw token in the DB — clicking
    // Copy Link on a pending row calls the Resend endpoint (which
    // rotates the token, extends 7d, returns a fresh raw_token). This
    // matches the "one-time secure token" security model.
    doAction(i.invitation_id, "resend");
  };

  if (!invites || invites.length === 0) {
    return (
      <div className="border border-dashed border-zinc-300 bg-zinc-50 p-6 text-center rounded-sm text-sm text-zinc-500"
           data-testid="invites-empty-state">
        No pending invitations. Add a team user to send a new invitation.
      </div>
    );
  }

  return (
    <table className="w-full text-sm border border-zinc-200" data-testid="invites-table">
      <thead className="bg-zinc-50 text-zinc-600">
        <tr>
          <th className="p-2 text-left">Name</th>
          <th className="p-2 text-left">Email</th>
          <th className="p-2 text-left">Roles</th>
          <th className="p-2 text-left">Invited By</th>
          <th className="p-2 text-left">Created</th>
          <th className="p-2 text-left">Expires</th>
          <th className="p-2 text-left">Status</th>
          <th className="p-2 text-right">Actions</th>
        </tr>
      </thead>
      <tbody>
        {invites.map(i => {
          const expired = isExpired(i);
          const displayStatus = expired && i.status === "pending" ? "expired" : i.status;
          const isPendingActive = i.status === "pending" && !expired;
          const isExpiredNow = i.status === "pending" && expired;
          return (
            <tr key={i.invitation_id} className="border-t border-zinc-200"
                data-testid={`row-inv-${i.invitation_id}`}>
              <td className="p-2">{[i.first_name, i.last_name].filter(Boolean).join(" ") || "—"}</td>
              <td className="p-2 font-mono">{i.email}</td>
              <td className="p-2">
                {(i.assigned_roles || []).map(rid => roleMap[rid] || rid).join(", ") || "—"}
              </td>
              <td className="p-2 text-zinc-500">{i.invited_by_email || "—"}</td>
              <td className="p-2 text-zinc-500">{i.created_at?.slice(0, 10)}</td>
              <td className="p-2 text-zinc-500">{i.expires_at?.slice(0, 10)}</td>
              <td className="p-2">
                <span className={
                  displayStatus === "pending"   ? "text-amber-600 font-medium" :
                  displayStatus === "accepted"  ? "text-emerald-600 font-medium" :
                  displayStatus === "expired"   ? "text-red-600 font-medium" :
                                                  "text-zinc-500"
                } data-testid={`inv-status-${i.invitation_id}`}>{displayStatus}</span>
              </td>
              <td className="p-2 text-right space-x-1 whitespace-nowrap">
                {isPendingActive && (
                  <>
                    <button className="gf-btn gf-btn-outline text-xs"
                            onClick={() => copyLink(i)}
                            data-testid={`btn-copy-link-${i.invitation_id}`}
                            title="Rotate the token and open the copy modal">
                      <Copy size={12} /> Copy link
                    </button>
                    <button className="gf-btn gf-btn-outline text-xs"
                            onClick={() => doAction(i.invitation_id, "cancel")}
                            data-testid={`btn-cancel-${i.invitation_id}`}>Cancel</button>
                  </>
                )}
                {isExpiredNow && (
                  <button className="gf-btn gf-btn-primary text-xs"
                          onClick={() => doAction(i.invitation_id, "resend")}
                          data-testid={`btn-regenerate-${i.invitation_id}`}
                          title="Rotate the token, extend expiry by 7 days">
                    Generate New Invitation
                  </button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function RolesTab({ roles, permissions }) {
  return (
    <div className="space-y-3" data-testid="roles-tab">
      {roles.map(r => (
        <div key={r.role_id} className="border border-zinc-200 p-3">
          <div className="flex items-baseline justify-between">
            <div>
              <div className="font-semibold">{r.name} {r.is_system && <span className="text-xs text-zinc-400 ml-2">(system)</span>}</div>
              <div className="text-xs text-zinc-500">{r.description}</div>
            </div>
            <div className="text-xs text-zinc-500">{(r.permissions || []).length} permissions</div>
          </div>
          <div className="mt-2 flex flex-wrap gap-1">
            {(r.permissions || []).slice(0, 40).map(p => (
              <code key={p} className="text-[10px] bg-zinc-100 px-1.5 py-0.5">{p}</code>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function PermissionMatrix({ roles, permissions }) {
  // group by category
  const groups = {};
  permissions.forEach(p => { (groups[p.group] = groups[p.group] || []).push(p.code); });
  return (
    <div className="overflow-x-auto" data-testid="permission-matrix">
      <table className="text-xs border border-zinc-200">
        <thead className="bg-zinc-50">
          <tr>
            <th className="p-2 text-left sticky left-0 bg-zinc-50">Permission</th>
            {roles.map(r => <th key={r.role_id} className="p-2 text-center whitespace-nowrap">{r.name}</th>)}
          </tr>
        </thead>
        <tbody>
          {Object.entries(groups).map(([grp, codes]) => (
            <React.Fragment key={grp}>
              <tr className="bg-zinc-100"><td colSpan={roles.length + 1} className="p-1 font-semibold">{grp}</td></tr>
              {codes.map(code => (
                <tr key={code} className="border-t border-zinc-200">
                  <td className="p-1 font-mono sticky left-0 bg-white">{code}</td>
                  {roles.map(r => (
                    <td key={r.role_id} className="p-1 text-center">
                      {(r.permissions || []).includes(code) ? "●" : ""}
                    </td>
                  ))}
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AuditTab({ events }) {
  return (
    <table className="w-full text-xs border border-zinc-200" data-testid="audit-table">
      <thead className="bg-zinc-50 text-zinc-600">
        <tr>
          <th className="p-2 text-left">Timestamp</th>
          <th className="p-2 text-left">Action</th>
          <th className="p-2 text-left">Actor</th>
          <th className="p-2 text-left">Target</th>
        </tr>
      </thead>
      <tbody>
        {events.slice(0, 200).map((e, i) => (
          <tr key={i} className="border-t border-zinc-200">
            <td className="p-2 font-mono text-[11px]">{e.timestamp?.slice(0, 19)}</td>
            <td className="p-2">{e.action}</td>
            <td className="p-2 text-zinc-500">{e.actor_email}</td>
            <td className="p-2 font-mono text-[11px]">{e.target_user_id || ""}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function InviteModal({ roles, onClose, onCreated }) {
  const [email, setEmail] = useState("");
  const [firstName, setFirst] = useState("");
  const [lastName, setLast] = useState("");
  const [primaryRole, setPrimaryRole] = useState("");
  const [additionalRoles, setAdditionalRoles] = useState([]);
  const [notes, setNotes] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [overrideAllow, setOverrideAllow] = useState("");
  const [overrideDeny, setOverrideDeny] = useState("");
  const [busy, setBusy] = useState(false);

  const selectableRoles = roles || [];
  const additionalCandidates = selectableRoles.filter(r => r.role_id !== primaryRole);

  const toggleAdditional = (id) => {
    setAdditionalRoles(rs => rs.includes(id) ? rs.filter(x => x !== id) : [...rs, id]);
  };

  const submit = async () => {
    if (!email.includes("@")) return toast.error("Valid email required");
    if (!primaryRole) return toast.error("Pick a primary role");
    setBusy(true);
    try {
      const roleIds = [primaryRole, ...additionalRoles.filter(r => r !== primaryRole)];
      const overrides = { allow: [], deny: [] };
      if (showAdvanced) {
        overrides.allow = overrideAllow.split(",").map(s => s.trim()).filter(Boolean);
        overrides.deny  = overrideDeny.split(",").map(s => s.trim()).filter(Boolean);
      }
      const { data } = await api.post("/rbac/invitations", {
        email,
        first_name: firstName,
        last_name: lastName,
        role_ids: roleIds,
        permission_overrides: overrides,
        expires_in_days: 7,
        notes,
      });
      onCreated(data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invite failed");
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4"
         data-testid="invite-modal" onClick={onClose}>
      <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto"
           onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-zinc-200 flex items-center gap-2">
          <UserPlus size={18} weight="bold" className="text-zinc-700" />
          <h3 className="font-semibold text-base">Add Team User</h3>
        </div>
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-zinc-500 block mb-1">First name</label>
              <input className="gf-input w-full text-sm" placeholder="Jane"
                     value={firstName} onChange={e => setFirst(e.target.value)}
                     data-testid="input-invite-first-name" />
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-zinc-500 block mb-1">Last name</label>
              <input className="gf-input w-full text-sm" placeholder="Smith"
                     value={lastName} onChange={e => setLast(e.target.value)}
                     data-testid="input-invite-last-name" />
            </div>
          </div>

          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-zinc-500 block mb-1">
              Email address <span className="text-red-500">*</span>
            </label>
            <input className="gf-input w-full text-sm" placeholder="jane@company.com"
                   value={email} onChange={e => setEmail(e.target.value)}
                   data-testid="input-invite-email" type="email" />
          </div>

          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-zinc-500 block mb-1">
              Role <span className="text-red-500">*</span>
            </label>
            <select className="gf-input w-full text-sm" value={primaryRole}
                    onChange={e => setPrimaryRole(e.target.value)}
                    data-testid="select-primary-role">
              <option value="">— choose a role —</option>
              {selectableRoles.map(r => (
                <option key={r.role_id} value={r.role_id}>{r.name}</option>
              ))}
            </select>
            {primaryRole && (ROLE_DESCRIPTIONS[primaryRole] || selectableRoles.find(r => r.role_id === primaryRole)?.description) && (
              <div className="text-xs text-zinc-500 mt-1" data-testid="primary-role-desc">
                {ROLE_DESCRIPTIONS[primaryRole] || selectableRoles.find(r => r.role_id === primaryRole).description}
              </div>
            )}
          </div>

          {additionalCandidates.length > 0 && (
            <details data-testid="additional-roles-details">
              <summary className="text-xs font-mono uppercase tracking-widest text-zinc-500 cursor-pointer">
                Additional roles <span className="lowercase text-zinc-400">(optional)</span>
              </summary>
              <div className="space-y-1 max-h-40 overflow-y-auto border border-zinc-200 p-2 mt-2 rounded-sm">
                {additionalCandidates.map(r => (
                  <label key={r.role_id} className="flex items-start gap-2 text-sm py-1">
                    <input type="checkbox" checked={additionalRoles.includes(r.role_id)}
                           onChange={() => toggleAdditional(r.role_id)}
                           data-testid={`chk-role-${r.role_id}`} />
                    <span>
                      <span className="font-medium">{r.name}</span>
                      <span className="text-xs text-zinc-500 block">
                        {ROLE_DESCRIPTIONS[r.role_id] || r.description || `${(r.permissions || []).length} permissions`}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </details>
          )}

          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-zinc-500 block mb-1">
              Notes <span className="text-zinc-400 lowercase">(optional, for your records)</span>
            </label>
            <textarea className="gf-input w-full text-sm" rows={2}
                      value={notes} onChange={e => setNotes(e.target.value)}
                      placeholder="e.g. Backfill for Sarah while she's on leave"
                      data-testid="input-invite-notes" />
          </div>

          <button
            type="button"
            className="text-xs text-zinc-500 hover:text-zinc-800"
            onClick={() => setShowAdvanced(v => !v)}
            data-testid="btn-toggle-advanced"
          >
            {showAdvanced ? "− Hide advanced permission overrides" : "+ Advanced: permission overrides"}
          </button>
          {showAdvanced && (
            <div className="border border-zinc-200 rounded-sm p-3 space-y-2 bg-zinc-50">
              <div className="text-xs text-zinc-500">
                Comma-separated permission codes to explicitly allow or deny
                on top of the assigned roles. Leave blank if you&apos;re not sure.
              </div>
              <input className="gf-input w-full text-sm font-mono"
                     placeholder="allow: e.g. reports.download, users.view"
                     value={overrideAllow}
                     onChange={e => setOverrideAllow(e.target.value)}
                     data-testid="input-override-allow" />
              <input className="gf-input w-full text-sm font-mono"
                     placeholder="deny: e.g. transactions.delete"
                     value={overrideDeny}
                     onChange={e => setOverrideDeny(e.target.value)}
                     data-testid="input-override-deny" />
            </div>
          )}

          <div className="text-xs bg-amber-50 border border-amber-200 rounded-sm p-2 text-amber-900">
            <strong>Email delivery is currently disabled.</strong> After
            creating the invitation, copy the secure link and share it
            with your teammate manually. Links expire in 7 days and can
            only be used once.
          </div>
        </div>
        <div className="flex justify-end gap-2 p-3 border-t border-zinc-200 bg-zinc-50 sticky bottom-0">
          <button className="gf-btn gf-btn-outline" onClick={onClose}
                  data-testid="btn-invite-cancel">Cancel</button>
          <button className="gf-btn gf-btn-primary" onClick={submit} disabled={busy || !primaryRole || !email.includes("@")}
                  data-testid="btn-invite-submit">
            {busy ? "Creating…" : "Create Invitation"}
          </button>
        </div>
      </div>
    </div>
  );
}

function InviteResultModal({ result, roles = [], onClose }) {
  const url = `${window.location.origin}${result.accept_path || `/invite/accept?token=${result.raw_token}`}`;
  const teamLogin = teamLoginUrl();
  const roleMap = Object.fromEntries((roles || []).map(r => [r.role_id, r.name]));
  const displayName = [result.first_name, result.last_name].filter(Boolean).join(" ").trim();
  const expiresLabel = result.expires_at
    ? new Date(result.expires_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
    : "—";
  const copy = () => {
    navigator.clipboard?.writeText(url);
    toast.success("Invitation link copied");
  };
  const copyLogin = () => {
    navigator.clipboard?.writeText(teamLogin);
    toast.success("Permanent team login URL copied");
  };
  const openLink = () => window.open(url, "_blank", "noopener");
  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4"
         data-testid="invite-result-modal" onClick={onClose}>
      <div className="bg-white w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-zinc-200 flex items-center gap-2">
          <CheckCircle size={20} weight="fill" className="text-emerald-600" />
          <h3 className="font-semibold text-base" data-testid="invite-result-title">
            Team User Invitation Created
          </h3>
        </div>
        <div className="p-4 space-y-3 text-sm">
          <dl className="grid grid-cols-3 gap-y-2 text-sm">
            <dt className="text-xs font-mono uppercase tracking-widest text-zinc-500">User</dt>
            <dd className="col-span-2" data-testid="invite-result-name">{displayName || "—"}</dd>

            <dt className="text-xs font-mono uppercase tracking-widest text-zinc-500">Email</dt>
            <dd className="col-span-2 font-mono" data-testid="invite-result-email">{result.email}</dd>

            <dt className="text-xs font-mono uppercase tracking-widest text-zinc-500">Roles</dt>
            <dd className="col-span-2" data-testid="invite-result-roles">
              {(result.assigned_roles || []).map(rid => roleMap[rid] || rid).join(", ") || "—"}
            </dd>

            <dt className="text-xs font-mono uppercase tracking-widest text-zinc-500">Expires</dt>
            <dd className="col-span-2" data-testid="invite-result-expires">{expiresLabel}</dd>
          </dl>

          <div className="border border-amber-200 bg-amber-50 rounded-sm p-3 text-amber-900 text-xs">
            <strong>Email delivery is currently disabled.</strong> Copy
            and send this secure invitation link manually. The link
            expires in 7 days and can only be used once.
          </div>

          <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-2 font-mono text-xs break-all"
               data-testid="invite-link">{url}</div>

          <div className="flex flex-wrap gap-2">
            <button className="gf-btn gf-btn-primary text-xs"
                    onClick={copy}
                    data-testid="btn-copy-invite">
              <Copy size={12} weight="bold" /> Copy Invitation Link
            </button>
            <button className="gf-btn gf-btn-outline text-xs"
                    onClick={openLink}
                    data-testid="btn-open-invite">
              <ArrowSquareOut size={12} weight="bold" /> Open Invitation Link
            </button>
          </div>

          <div className="border-t border-zinc-200 pt-3 mt-3">
            <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">
              Permanent Team Login
            </div>
            <div className="text-xs text-zinc-600 mb-2">
              Once they accept, teammates sign in here for all future access.
              The invitation link above only works once.
            </div>
            <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-2 font-mono text-xs break-all"
                 data-testid="invite-team-login-url">{teamLogin}</div>
            <button className="gf-btn gf-btn-outline text-xs mt-2"
                    onClick={copyLogin}
                    data-testid="btn-copy-team-login">
              <Copy size={12} weight="bold" /> Copy Login Link
            </button>
          </div>
        </div>
        <div className="flex justify-end p-3 border-t border-zinc-200 bg-zinc-50">
          <button className="gf-btn gf-btn-primary" onClick={onClose}
                  data-testid="btn-invite-result-close">Done</button>
        </div>
      </div>
    </div>
  );
}


/**
 * HowToAddBox — quick step-by-step help for operators, matching the
 * Phase 2.3 spec. Collapsible so it doesn't clutter the page for
 * repeat visitors.
 */
function HowToAddBox() {
  const [open, setOpen] = useState(false);
  return (
    <details
      className="border border-zinc-200 bg-zinc-50 rounded-sm p-3"
      data-testid="how-to-add-box"
      open={open}
      onToggle={(e) => setOpen(e.target.open)}
    >
      <summary className="cursor-pointer text-sm text-zinc-700 flex items-center gap-2 list-none">
        <Info size={14} weight="bold" className="text-blue-600" />
        <span className="font-medium">How to add a team user</span>
        <span className="text-xs text-zinc-500 ml-2">
          {open ? "hide" : "show"} the 7-step walkthrough
        </span>
      </summary>
      <ol className="mt-3 pl-5 list-decimal text-sm text-zinc-700 space-y-1">
        <li>Click <strong>Add Team User</strong> (top-right).</li>
        <li>Enter the user&apos;s email address.</li>
        <li>Assign the required role (and any additional roles).</li>
        <li>Click <strong>Create Invitation</strong>.</li>
        <li>Copy the secure invitation link from the success modal.</li>
        <li>Send the link to the teammate — they open it and accept.</li>
        <li>They sign in with Google using that email and only see the modules their role allows.</li>
      </ol>
    </details>
  );
}


/**
 * PreviewRoleControl — Phase 2.2.
 *
 * Super-Admin-only dropdown that lets the operator preview how the
 * admin UI renders for a given role. Selecting a role calls
 * `POST /rbac/preview/start` (writes an audit row) and swaps the
 * frontend permission set to the target role's — the backend session
 * is UNCHANGED. Mutations are blocked in the axios request
 * interceptor while preview is active.
 */
function PreviewRoleControl({ roles }) {
  const { isRealSuperAdmin, isPreviewing, previewRoleId, startPreview, endPreview } = useRbac();
  const [busy, setBusy] = useState(false);
  if (!isRealSuperAdmin && !isPreviewing) return null;

  const onChange = async (e) => {
    const rid = e.target.value;
    if (!rid) return;
    setBusy(true);
    try {
      await startPreview(rid);
      toast.success(`Now previewing as ${rid.replace(/^role_/, "").replace(/_/g, " ")}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start preview");
    } finally {
      setBusy(false);
    }
  };

  const stop = async () => {
    setBusy(true);
    try {
      await endPreview();
      toast.success("Exited preview mode");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-2" data-testid="preview-role-control">
      {isPreviewing ? (
        <button
          className="gf-btn gf-btn-outline text-xs"
          onClick={stop}
          disabled={busy}
          data-testid="preview-role-stop"
          title="Exit preview and return to your Super Admin view"
        >
          Exit preview
        </button>
      ) : (
        <>
          <span className="text-xs font-mono uppercase tracking-widest text-zinc-500">
            Preview as
          </span>
          <select
            className="gf-input text-xs"
            value={previewRoleId || ""}
            onChange={onChange}
            disabled={busy}
            data-testid="preview-role-select"
            style={{ minWidth: 180 }}
          >
            <option value="">— pick a role —</option>
            {roles.filter((r) => r.role_id !== "role_super_admin").map((r) => (
              <option key={r.role_id} value={r.role_id}>
                {r.name}
              </option>
            ))}
          </select>
        </>
      )}
    </div>
  );
}
